import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AppContext } from '../../context/AppContext';
import { AuthContext } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';

const ReplyEnvelope = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!supabase) { setLoading(false); return; }
      const { data } = await supabase
        .from('invitations')
        .select('*')
        .eq('unique_token', id)
        .single();
      if (data) {
        setInvite(data);
        // Mark reply as opened
        if (data.sender_id === user?.id && !data.reply_opened_at) {
          await supabase
            .from('invitations')
            .update({ reply_opened_at: new Date().toISOString() })
            .eq('unique_token', id);
        }
      }
      setLoading(false);
    };
    load();
  }, [id, user?.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-pink-50 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-pink-50 flex items-center justify-center p-6 text-center">
        <div>
          <div className="text-5xl mb-4">😕</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Reply not found</h2>
          <button onClick={() => navigate('/home')} className="mt-4 text-pink-500 font-semibold">Go to DayClap</button>
        </div>
      </div>
    );
  }

  const isSingles = invite.flow_type === 'singles';

  // Sealed envelope state
  if (!opened) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50 via-white to-rose-50 flex flex-col items-center justify-center p-6 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden -z-10">
          <div className="absolute top-10 left-10 w-40 h-40 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40" />
          <div className="absolute top-20 right-10 w-40 h-40 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm text-center"
        >
          <img src="https://cloud.sharemyimage.com/2026/08/16/DayClap-Logo.png" alt="DayClap" className="h-10 mx-auto mb-6" />

          <p className="text-sm text-gray-500 mb-4">💌 You received a reply!</p>

          {/* Animated sealed envelope */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            className="relative mx-auto mb-8"
          >
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
              className="w-48 h-36 mx-auto relative"
            >
              {/* Envelope body */}
              <div className="absolute inset-0 bg-gradient-to-b from-amber-100 to-amber-200 rounded-2xl shadow-xl border border-amber-300" />
              {/* Envelope flap */}
              <div className="absolute top-0 left-0 right-0 h-1/2 bg-gradient-to-b from-amber-200 to-amber-100 rounded-t-2xl border border-amber-300"
                style={{ clipPath: 'polygon(0 0, 50% 100%, 100% 0)' }} />
              {/* Seal */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-red-500 rounded-full flex items-center justify-center shadow-lg z-10">
                <span className="text-white text-lg">💌</span>
              </div>
              {/* From label */}
              <div className="absolute bottom-3 left-0 right-0 text-center">
                <p className="text-xs text-amber-700 font-medium">From {invite.recipient_name}</p>
              </div>
            </motion.div>
          </motion.div>

          <h2 className="text-2xl font-bold text-gray-800 mb-2">A Reply Awaits! 💌</h2>
          <p className="text-gray-500 mb-8 text-sm">
            {invite.recipient_name} responded to your invitation
          </p>

          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setOpened(true)}
            className="bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 px-10 rounded-full shadow-xl shadow-pink-200 text-lg"
          >
            Open Reply ✨
          </motion.button>
        </motion.div>
      </div>
    );
  }

  // Opened state — show just the question + answer
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gradient-to-b from-pink-50 via-white to-rose-50 flex flex-col items-center justify-center p-6"
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="w-full max-w-sm"
      >
        <img src="https://cloud.sharemyimage.com/2026/08/16/DayClap-Logo.png" alt="DayClap" className="h-10 mx-auto mb-8" />

        {/* The reply card */}
        <div className="bg-white rounded-3xl p-8 shadow-xl border border-pink-100 text-center">
          {/* Question */}
          {isSingles && invite.invitation_question && (
            <div className="mb-6">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2">Your Question</p>
              <p className="text-lg font-semibold text-gray-800 italic">"{invite.invitation_question}"</p>
            </div>
          )}

          {/* Response badge */}
          {isSingles && invite.reply_response && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.4 }}
              className="mb-6"
            >
              <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-lg font-bold ${
                invite.reply_response === 'yes' ? 'bg-green-100 text-green-700' :
                invite.reply_response === 'maybe' ? 'bg-amber-100 text-amber-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {invite.reply_response === 'yes' && '❤️ YES'}
                {invite.reply_response === 'maybe' && '🤔 MAYBE'}
                {invite.reply_response === 'no' && '❌ NO'}
              </div>
            </motion.div>
          )}

          {/* Reply message */}
          {invite.reply_message && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-pink-50 rounded-2xl p-5"
            >
              <p className="text-xs text-pink-400 font-semibold uppercase tracking-wider mb-2">
                {invite.recipient_name}'s Reply
              </p>
              <p className="text-gray-700 italic leading-relaxed text-lg">"{invite.reply_message}"</p>
            </motion.div>
          )}

          {/* No reply message */}
          {!invite.reply_message && !invite.reply_response && (
            <p className="text-gray-400 text-sm">No reply message yet</p>
          )}
        </div>

        <button
          onClick={() => navigate('/home')}
          className="w-full mt-6 text-gray-500 font-medium py-3 hover:text-pink-500 transition-colors"
        >
          Back to DayClap
        </button>
      </motion.div>
    </motion.div>
  );
};

export default ReplyEnvelope;